/**
 * The words for a thing on the ring, and the small box they are shown in.
 *
 * The calendar page and the landing page both draw a ring, and both answer a
 * hover or a tap with the same sentence — a name and a date, a cluster's
 * names, a day. One place for the sentence means the demo tells no story of
 * its own, and one place for the box means it sits the same way over both.
 */
import type { Point } from '@circle/shared';
import type { Scene, SceneEntry } from '../core/scene';
import { detailText, formatDayMonth } from '../core/format';

/** What the sentence needs to know about the ring it is describing. */
export interface DescribeContext {
  scene: Scene;
  /** The year the ring is showing, for an age. */
  year: number;
  today: Date;
}

/** One line for an entry, or nothing for the kinds that have nothing to say. */
export function describeEntry(entry: SceneEntry, ctx: DescribeContext): string {
  switch (entry.kind) {
    case 'day':
      return formatDayMonth(entry.ctx.month as number, entry.ctx.day as number);
    case 'cluster': {
      const ids: string[] = entry.eventIds ?? [];
      return ids
        .map((id) => ctx.scene.eventById(id)?.title)
        .filter((title): title is string => Boolean(title))
        .join(', ');
    }
    case 'ringMark':
    case 'float':
    case 'leader': {
      const event = entry.ctx.event;
      if (!event) return '';
      return `${event.title} — ${detailText(event, ctx.year, ctx.today)}`;
    }
    case 'today':
      return `Today, ${formatDayMonth(ctx.today.getMonth() + 1, ctx.today.getDate())}`;
    default:
      return '';
  }
}

/** The box: a `.tooltip` in the ring's host, placed above a point in host space. */
export class Tooltip {
  readonly root: HTMLElement;

  constructor(private readonly host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'tooltip';
    this.root.dataset.visible = 'false';
    host.appendChild(this.root);
  }

  /** Show `text` above `at`, or hide when there is nothing to say. */
  show(text: string, at: Point): void {
    if (!text) {
      this.hide();
      return;
    }
    this.root.textContent = text;
    this.root.style.transform = `translate(${at.x}px, ${at.y}px) translate(-50%, -160%)`;
    this.root.dataset.visible = 'true';
  }

  /**
   * Show it over an entry's own box rather than under a pointer — what a tap
   * has to go on, since a finger never hovers.
   */
  showOn(text: string, entry: SceneEntry): void {
    const box = entry.el.getBoundingClientRect();
    const host = this.host.getBoundingClientRect();
    this.show(text, {
      x: box.left + box.width / 2 - host.left,
      y: box.top + box.height / 2 - host.top,
    });
  }

  hide(): void {
    this.root.dataset.visible = 'false';
  }

  destroy(): void {
    this.root.remove();
  }
}
